import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CircleNotch, EnvelopeSimple, Scales } from '@phosphor-icons/react';
import { platformApi } from '@/lib/platformApi';

/**
 * Recovering the Platform Console without a developer.
 *
 * The screen deliberately shows the SAME confirmation whether or not the address belongs to
 * an admin. Anything else — a different message, a different delay, a visible error — turns
 * this page into a way to discover who operates the platform.
 */
export default function PlatformForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isPending, setIsPending] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (isPending || !email) return;
    setIsPending(true);
    try {
      await platformApi.post('/auth/forgot-password', { email });
    } catch {
      // Swallowed on purpose: a failure that is visible only for real addresses would leak
      // exactly what the generic message is protecting.
    } finally {
      setIsPending(false);
      setSent(true);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-plat-bg px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-center gap-2">
          <Scales className="h-7 w-7 text-plat-info-text" weight="fill" />
          <span className="text-lg font-semibold text-plat-fg">Platform Console</span>
        </div>

        <div className="rounded-xl border border-plat-border bg-plat-surface p-6">
          {sent ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <EnvelopeSimple className="h-6 w-6 text-plat-info-text" weight="fill" />
                <h2 className="text-[18px] font-semibold text-plat-fg">Check your email</h2>
              </div>
              <p className="text-sm text-plat-secondary">
                If an account with that email exists, a reset link is on its way. The link works
                once and expires in an hour.
              </p>
              <p className="text-xs text-plat-muted">
                Nothing arrived? Check the address and try again — and make sure your mail
                provider is not holding it.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <h2 className="text-[18px] font-semibold text-plat-fg">Reset your password</h2>
                <p className="mt-1 text-sm text-plat-secondary">
                  We will email you a link to set a new one.
                </p>
              </div>

              <div>
                <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-plat-secondary">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg placeholder:text-plat-faint dark:placeholder:text-plat-secondary focus:border-plat-focus focus:outline-none"
                  placeholder="you@example.com"
                />
              </div>

              <button
                type="submit"
                disabled={isPending}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-plat-accent-solid px-5 text-sm font-medium text-plat-on-accent transition hover:bg-plat-accent-solid-hover disabled:cursor-not-allowed disabled:bg-plat-neutral-soft disabled:text-plat-disabled"
              >
                {isPending ? (
                  <>
                    <CircleNotch className="h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  'Send reset link'
                )}
              </button>
            </form>
          )}

          <div className="mt-6 border-t border-plat-border pt-4">
            <Link
              to="/platform/login"
              className="inline-flex items-center gap-1.5 text-xs text-plat-muted transition hover:text-plat-secondary"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
